import { toStoredPaymentScheduleStage } from '@/api/fragments/paymentScheduleStageFields';
import { type StoredPaymentSchedule } from '@payaca/types/payment-schedule';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { gqlClient } from '@/api/graphql-client';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import { GetProposalInput, GetProposalQuery } from '@/gql/graphql';

/**
 * Whether one action can be taken, and the reasons it cannot. Repeated for
 * every action a surface asks about.
 */
export const ActionAvailabilityFragment = graphql(`
  fragment ActionAvailabilityFields on ActionAvailabilityResult {
    result
    reasons
  }
`);

/**
 * Selection set for a leaf line item within a proposal item group. Repeated at
 * every nesting level, so held in one place and spread wherever the
 * `ProposalItemGroupChild` union resolves to a `ProposalGroupLineItem`.
 */
export const ProposalGroupLineItemFragment = graphql(`
  fragment ProposalGroupLineItemFields on ProposalGroupLineItem {
    selected
    proposalLineItem {
      id
      publicId
      internalReference
      description
      quantity
      price {
        unitPriceExcTax {
          value
          currency {
            exponent
            code
          }
        }
      }
      totals {
        totalValue {
          value
          currency {
            exponent
            code
          }
        }
        discountValue {
          value
          currency {
            exponent
            code
          }
        }
      }
    }
  }
`);

/**
 * A proposal item group's own fields, excluding its `children`. GraphQL can't
 * express recursion, so the `children` tree must be unrolled to a fixed depth
 * by the consuming query — this fragment keeps each level's group selection
 * identical without recursing itself (which would be a validation error).
 */
export const ProposalItemGroupFragment = graphql(`
  fragment ProposalItemGroupFields on ProposalItemGroup {
    key
    title
    selectionMode
    selected
  }
`);

const GET_PROPOSAL = graphql(`
  query GetProposal($input: GetProposalInput!) {
    proposal(input: $input) {
      id
      documentType
      sentAt
      bouncedAt
      acceptedAt
      declinedAt
      reference
      customReference
      validForDays
      emailPreview {
        subject
      }
      customerContact {
        publicId
        name
        email
      }
      totals {
        subtotal {
          value
          currency {
            exponent
            code
          }
        }
        discount {
          value
          currency {
            exponent
            code
          }
        }
        tax {
          value
          currency {
            exponent
            code
          }
        }
        cis {
          value
          currency {
            exponent
            code
          }
        }
        total {
          value
          currency {
            exponent
            code
          }
        }
        totalPayable {
          value
          currency {
            exponent
            code
          }
        }
      }
      grants {
        id
        name
        description
        amount {
          value
          currency {
            exponent
            code
          }
        }
        availableAmount {
          value
          currency {
            exponent
            code
          }
        }
        source {
          id
        }
      }
      profitForecast {
        materials {
          value
          currency {
            exponent
            code
          }
        }
        labour {
          value
          currency {
            exponent
            code
          }
        }
        profit {
          value
          currency {
            exponent
            code
          }
        }
      }
      emailTemplate {
        id
        preview
      }
      actionAvailability {
        send {
          ...ActionAvailabilityFields
        }
        markAsSent {
          ...ActionAvailabilityFields
        }
        resend {
          ...ActionAvailabilityFields
        }
        markAsAccepted {
          ...ActionAvailabilityFields
        }
        markAsDeclined {
          ...ActionAvailabilityFields
        }
        removeAccepted {
          ...ActionAvailabilityFields
        }
        duplicate {
          ...ActionAvailabilityFields
        }
        replace {
          ...ActionAvailabilityFields
        }
      }
      paymentSchedule {
        stages {
          ...PaymentScheduleStageFields
        }
        explainer
      }
      depositAmount
      depositPercentage
      effectivePaymentSchedule {
        stages {
          ...DerivedPaymentStageFields
        }
      }
      attachments {
        id
        fileName
        mimeType
        extension
        url
        thumbnailUrl
      }
      marketingAttachments {
        id
        fileName
        mimeType
        url
      }
      sections {
        __typename
        ... on ProposalContentSection {
          __typename
          key
          title
          content
        }
        ... on ProposalItemGroupSection {
          __typename
          key
          title
          groups {
            __typename
            ...ProposalItemGroupFields
            children {
              __typename
              ... on ProposalItemGroup {
                ...ProposalItemGroupFields
                children {
                  __typename
                  ...ProposalGroupLineItemFields
                }
              }
              ...ProposalGroupLineItemFields
            }
          }
        }
      }
    }
  }
`);

const getProposalQueryOptions = (id?: GetProposalInput['id']) =>
  queryOptions({
    enabled: !!id,
    queryKey: proposalKeys.proposal(id!),
    queryFn: () => {
      return gqlClient.request(GET_PROPOSAL, { input: { id: id! } });
    },
  });

export const useGetProposal = (id?: GetProposalInput['id']) => {
  const { data, ...rest } = useQuery(getProposalQueryOptions(id));

  return {
    data,
    proposal: data?.proposal,
    ...rest,
  };
};

/**
 * Subscribes to a slice of the Proposal, so the caller re-renders only when
 * that slice changes rather than on every write to the cached Proposal.
 */
export const useSelectProposal = <TSelected>(
  id: GetProposalInput['id'] | undefined,
  select: (proposal: GetProposalQuery['proposal']) => TSelected
) => {
  const { data, isLoading } = useQuery({
    ...getProposalQueryOptions(id),
    select: (result) => select(result.proposal),
  });

  return { data, isLoading };
};

export const useGetProposalEmailTemplate = (id?: GetProposalInput['id']) => {
  const { data, ...rest } = useGetProposal(id);

  return {
    data,
    emailTemplate: data?.proposal.emailTemplate,
    ...rest,
  };
};

export const useGetProposalPaymentSchedule = (id?: GetProposalInput['id']) => {
  const { data, ...rest } = useGetProposal(id);
  const gqlPaymentSchedule = data?.proposal.paymentSchedule;
  const paymentSchedule = useMemo(
    (): StoredPaymentSchedule | null | undefined =>
      gqlPaymentSchedule && {
        stages: gqlPaymentSchedule.stages.map(toStoredPaymentScheduleStage),
        explainer: gqlPaymentSchedule.explainer,
      },
    [gqlPaymentSchedule]
  );

  return {
    data,
    paymentSchedule,
    ...rest,
  };
};

export const useGetProposalFinancials = (id?: GetProposalInput['id']) => {
  const { data, ...rest } = useGetProposal(id);

  return {
    data,
    totals: data?.proposal.totals,
    grants: data?.proposal.grants,
    profitForecast: data?.proposal.profitForecast,
    effectivePaymentSchedule: data?.proposal.effectivePaymentSchedule,
    ...rest,
  };
};

/** A single invoice grant attached to a Proposal. */
export type TProposalGrant = GetProposalQuery['proposal']['grants'][number];

export const useGetProposalAttachments = (id?: GetProposalInput['id']) => {
  const { data, ...rest } = useGetProposal(id);

  return {
    data,
    attachments: data?.proposal.attachments,
    marketingAttachments: data?.proposal.marketingAttachments,
    ...rest,
  };
};

export type ProposalSectionsQuery = GetProposalQuery['proposal']['sections'];

/** A single file attachment on a Proposal. */
export type TProposalAttachment =
  GetProposalQuery['proposal']['attachments'][number];

export type TMarketingAttachment =
  GetProposalQuery['proposal']['marketingAttachments'][number];

/** A single section, narrowed to the rich-text content variant of the union. */
export type ProposalContentSectionQuery = Extract<
  ProposalSectionsQuery[number],
  { __typename?: 'ProposalContentSection' }
>;

export type ProposalItemGroupSectionQuery = Extract<
  ProposalSectionsQuery[number],
  { __typename?: 'ProposalItemGroupSection' }
>;

export const useGetProposalSections = (id?: GetProposalInput['id']) => {
  const { data, ...rest } = useGetProposal(id);

  return {
    data,
    proposalSections: data?.proposal.sections,
    ...rest,
  };
};

export const useGetProposalEmailPreview = (id?: GetProposalInput['id']) => {
  const { data, ...rest } = useGetProposal(id);

  return {
    data,
    emailPreview: data?.proposal.emailPreview,
    reference: data?.proposal.customReference ?? data?.proposal.reference,
    ...rest,
  };
};
